import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactTagsInput = ({ 
  selectedTags = [],
  onTagsChange = () => {},
  className = '' 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Predefined tag suggestions
  const tagSuggestions = [
    'Family', 'Friend', 'Work', 'Client', 'Colleague', 'Business',
    'Personal', 'Important', 'VIP', 'Networking', 'School', 'University',
    'Neighbor', 'Doctor', 'Service Provider', 'Vendor', 'Partner',
    'Team Member', 'Manager', 'Customer', 'Supplier', 'Contractor'
  ];

  useEffect(() => {
    if (inputValue?.trim()) {
      const filtered = tagSuggestions?.filter(tag => 
        tag?.toLowerCase()?.includes(inputValue?.toLowerCase()) &&
        !selectedTags?.includes(tag)
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered?.length > 0);
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  }, [inputValue, selectedTags]);

  const handleInputChange = (e) => {
    setInputValue(e?.target?.value);
  };

  const handleInputKeyDown = (e) => {
    if (e?.key === 'Enter' || e?.key === ',') {
      e?.preventDefault();
      addTag(inputValue?.trim());
    } else if (e?.key === 'Backspace' && !inputValue && selectedTags?.length > 0) {
      removeTag(selectedTags?.[selectedTags?.length - 1]);
    }
  };

  const addTag = (tag) => {
    if (tag && !selectedTags?.includes(tag)) {
      const newTags = [...selectedTags, tag];
      onTagsChange(newTags);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove) => {
    const newTags = selectedTags?.filter(tag => tag !== tagToRemove);
    onTagsChange(newTags);
  };

  const handleSuggestionClick = (suggestion) => {
    addTag(suggestion);
    inputRef?.current?.focus();
  };

  const handleInputFocus = () => {
    if (inputValue?.trim()) {
      setShowSuggestions(filteredSuggestions?.length > 0);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicking
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-text-primary">
        Tags
      </label>
      {/* Tags Display and Input */}
      <div className="relative">
        <div className="min-h-[42px] p-2 border border-border rounded-md bg-surface focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Selected Tags */}
            {selectedTags?.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 bg-primary text-primary-foreground text-sm rounded-md"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="ml-1 hover:bg-primary/80 rounded-full p-0.5 transition-micro"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
            
            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={selectedTags?.length === 0 ? "Add tags (press Enter or comma to add)" : "Add more tags..."}
              className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-text-primary placeholder-text-secondary"
            />
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-md shadow-modal z-300 max-h-48 overflow-y-auto">
            {filteredSuggestions?.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-3 py-2 text-left hover:bg-muted transition-micro text-text-primary border-b border-border last:border-b-0"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Quick Add Buttons */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-text-secondary">Quick add:</span>
        {['Family', 'Friend', 'Work', 'Important']?.map((quickTag) => (
          !selectedTags?.includes(quickTag) && (
            <Button
              key={quickTag}
              variant="outline"
              size="xs"
              onClick={() => addTag(quickTag)}
            >
              {quickTag}
            </Button>
          )
        ))}
      </div>
      <p className="text-xs text-text-secondary">
        Use tags to organize and filter your contacts. Press Enter or comma to add a tag.
      </p>
    </div>
  );
};

export default ContactTagsInput;